import { CSSProperties, memo, ReactNode, useCallback } from 'react'
import classNames from 'classnames'
import { IconSpinner } from 'components/IconSpinner'
import { KeyCodeEnum } from 'enums/keyCodes'
import { useHotKey } from 'hooks/useHotKey'
import { stopBubbling } from 'utils/event'

import { DialogDesktop } from '../DialogDesktop'

export type TSize = 'sm' | 'nm' | 'lg' | 'xl' | 'xxl'

interface IProps {
  children: ReactNode
  className?: string
  /** Слот для нижнего блока диалога */
  footer?: JSX.Element
  formName?: string
  /** Слот для верхнего блока диалога */
  header?: JSX.Element
  id?: string
  /** Отображение кнопки закрытия окна */
  isCloseButton?: boolean
  /** Закрытие окна по нажатию на Esc */
  isCloseOnEsc?: boolean
  /** Закрытие окна по клику вне области этого окна */
  isCloseOnOutsideClick?: boolean
  /** Отображение окна как диалогового (прижатого к низу) */
  isDialogType?: boolean
  /** Отображение загрузки данных окна */
  isLoading?: boolean
  /** Отображение горизонтальных отступов контентной части */
  isOffsets?: boolean
  isOrder?: boolean
  isOrderCreate?: boolean
  noPadding?: boolean
  onClose?: () => void
  onSubmit?: () => void
  /** Размер окна */
  size?: TSize
  style?: CSSProperties
}

export const ModalDesktop = memo<IProps>(function ModalDesktop({
  children,
  className,
  footer,
  formName,
  header,
  id,
  isDialogType = false,
  isLoading = false,
  isCloseButton = true,
  isCloseOnEsc = true,
  isCloseOnOutsideClick = true,
  isOffsets = true,
  onClose,
  onSubmit,
  size = 'nm',
  style,
  isOrder,
  noPadding,
  isOrderCreate
}) {
  const handlePressOnEsc = useCallback(() => {
    if (isCloseOnEsc && onClose) {
      onClose()
    }
  }, [isCloseOnEsc, onClose])

  const content = () => {
    if (isLoading) {
      return <IconSpinner size="nm" />
    }

    return (
      <div
        className={classNames('flex w-full justify-center p-6 max-md:py-12', {
          'h-full justify-end p-0': isOrder,
          'h-[90%] p-0': isOrderCreate
        })}
      >
        <DialogDesktop
          footer={footer}
          header={header}
          id={id}
          isCloseButton={isCloseButton}
          isCloseOnOutsideClick={isCloseOnOutsideClick}
          isOffsets={isOffsets}
          isOrder={isOrder}
          noPadding={noPadding}
          onClose={onClose}
          size={size}
        >
          {children}
        </DialogDesktop>
      </div>
    )
  }

  const isForm = !!onSubmit
  const props = {
    className: classNames(
      'fixed left-0 top-0 z-modal flex size-full min-h-[100vh] items-center justify-center bg-[rgba(26,28,30,0.3)]',
      {
        'items-center': isDialogType || isLoading
      }
    ),
    id,
    onClick: stopBubbling,
    style
  }

  useHotKey(KeyCodeEnum.ESC, handlePressOnEsc)

  return isForm ? (
    <form {...props} name={formName} onSubmit={onSubmit}>
      {content()}
    </form>
  ) : (
    <div {...props}>{content()}</div>
  )
})
